import { useCallback, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { expandEndpoint, useApiConfig } from "@visioni/a3s-client-api/app";
import type { ClientApiFeatureSubPathParams } from "@visioni/a3s-client-api";
import {
  GetTimelapseVideoResponse,
  getTimelapseVideoEndpoint,
  type GetTimelapseVideoQueryParams,
  type RequestTimelapseVideoBody,
  type RequestTimelapseVideoResponse,
  type TimelapseVideoItem,
} from "../../client-messages/timelapse";
import { requestTimelapseVideoMutation, timelapseVideoStatusQuery } from "../queries";

// ============================================================================
// useTimelapseVideoStatus — polls the status endpoint until ready/failed
// ============================================================================

/**
 * Polling cadence (ms) while a job is `queued` or `processing`. Three seconds
 * is a sensible balance for the renderer worker — most jobs finish in tens of
 * seconds so the user sees progress without hammering the API.
 */
const ACTIVE_POLL_INTERVAL_MS = 3000;

export type UseTimelapseVideoStatusOptions = ClientApiFeatureSubPathParams &
  GetTimelapseVideoQueryParams & {
    /**
     * Disable the query while inputs are still settling (e.g. siteId/cameraKey
     * are missing from a deep link). Defaults to `true`.
     */
    enabled?: boolean;
  };

export interface TimelapseVideoStatusResult {
  /** Latest job state, or `null` when no job exists for this window yet. */
  readonly job: TimelapseVideoItem | null;
  /** True when server denied timelapse for this camera (`403`). */
  readonly isForbidden: boolean;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

function isHttpStatusError(error: unknown, status: number): boolean {
  if (typeof error !== "object" || error === null) return false;
  const candidate = error as { status?: unknown };
  return candidate.status === status;
}

/**
 * Status poller for an asynchronous timelapse export job.
 *
 * The status endpoint is keyed by camera + window (the same triple used at
 * request time), so the same hook can be wired up before the request
 * mutation runs — supporting the rehydration use case where a user navigates
 * away with a queued job and comes back, polling resumes automatically.
 *
 * The server returns **404** for "no export started yet" and **403** when
 * the camera is ineligible. Rather than throwing and tripping the global
 * `QueryCache.onError` handler on every page entry, the queryFn intercepts
 * both and resolves a structured success payload.
 */
export function useTimelapseVideoStatus(options: UseTimelapseVideoStatusOptions): TimelapseVideoStatusResult {
  const apiConfig = useApiConfig();
  const { enabled = true, startTime, endTime, cameraTimezone, ...pathParams } = options;
  const tenant = apiConfig.tenant;

  const queryParams = useMemo(() => ({ startTime, endTime, cameraTimezone }), [startTime, endTime, cameraTimezone]);

  // Reuse the same query key as `timelapseVideoStatusQuery` so the
  // mutation's `onSettled` invalidator (in `useRequestTimelapseVideo`) still
  // wakes us up via the standard predicate match.
  const baseOptions = timelapseVideoStatusQuery(apiConfig, pathParams, queryParams);

  const query = useQuery<{ job: TimelapseVideoItem | null; isForbidden: boolean }>({
    queryKey: baseOptions.queryKey,
    queryFn: async ({ signal }) => {
      if (!tenant) {
        // `enabled` already prevents us from getting here, but TypeScript
        // can't see that. Throwing keeps the type narrow without any
        // observable behaviour change.
        throw new Error("Timelapse status query requires an authenticated tenant");
      }
      const fullPath = { tenantId: tenant.tenantId, ...pathParams };
      const url = `${tenant.apiBaseUrl}${expandEndpoint(getTimelapseVideoEndpoint, {
        path: fullPath,
        query: queryParams,
      })}`;
      let response: Response;
      try {
        response = await apiConfig.authenticatedFetch(url, { signal });
      } catch (error: unknown) {
        if (isHttpStatusError(error, 403)) return { job: null, isForbidden: true };
        throw error;
      }

      // 404 = "no export job for this window yet". 403 = "camera not
      // eligible". Treat both as non-error control paths so query-cache
      // error handlers are not spammed on every page entry.
      if (response.status === 404) return { job: null, isForbidden: false };
      if (response.status === 403) return { job: null, isForbidden: true };

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Timelapse status request failed: ${String(response.status)}`);
      }

      const data: unknown = await response.json();
      return { job: GetTimelapseVideoResponse.parse(data).data, isForbidden: false };
    },
    enabled: enabled && tenant !== undefined,
    // Poll only while a server-acked job is still in flight (`queued` /
    // `processing`). Terminal states (`ready` / `failed`) and the
    // success-with-null "no job yet" case both quiet down to no polling.
    // The mutation's `onSettled` invalidator wakes the query back up the
    // moment a POST is fired, so the UI reacts immediately to a freshly
    // queued export.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      if (data.isForbidden || !data.job) return false;
      return data.job.status === "queued" || data.job.status === "processing" ? ACTIVE_POLL_INTERVAL_MS : false;
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Hold the latest `query.refetch` in a ref so the returned `refetch` has
  // a stable identity across renders. Without this, every consumer of
  // `useTimelapseVideoStatus` that depends on `refetch` (e.g. the
  // `requestExport` callback in `useTimelapseExport`) would be invalidated
  // on every render — defeating the surrounding `useCallback` memoization
  // and cascading instability through the hook graph.
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;
  const refetch = useCallback(() => {
    void refetchRef.current();
  }, []);

  return {
    job: query.data?.job ?? null,
    isForbidden: query.data?.isForbidden ?? false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error : null,
    refetch,
  };
}

// ============================================================================
// useRequestTimelapseVideo — POST mutation that enqueues / reuses a job
// ============================================================================

export type UseRequestTimelapseVideoOptions = ClientApiFeatureSubPathParams;

export interface RequestTimelapseVideoResult {
  /**
   * Fire the POST. Returns the server response (always wrapped in the
   * standard `{ data: TimelapseVideoItem }` envelope) and invalidates the
   * matching status query so the next poll picks up the freshly queued job.
   */
  readonly requestVideo: (body: RequestTimelapseVideoBody) => Promise<RequestTimelapseVideoResponse>;
  readonly isPending: boolean;
  readonly error: Error | null;
}

/**
 * Mutation hook for `POST /timelapse/video`. Idempotent on the server — the
 * service layer dedupes by camera + window key — so the UI can fire it on
 * every download click without worrying about duplicate work.
 */
export function useRequestTimelapseVideo(options: UseRequestTimelapseVideoOptions): RequestTimelapseVideoResult {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationConfig = useMemo(
    () =>
      apiConfig.tenant ?
        {
          tenantId: apiConfig.tenant.tenantId,
          apiBaseUrl: apiConfig.tenant.apiBaseUrl,
          authenticatedFetch: apiConfig.authenticatedFetch,
        }
      : { authenticatedFetch: apiConfig.authenticatedFetch },
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const mutationOptions = useMemo(
    () => requestTimelapseVideoMutation(mutationConfig, options),
    [mutationConfig, options],
  );

  const mutation = useMutation({
    mutationFn: mutationOptions.mutationFn,
    onSettled: () => {
      // Status query keys are keyed off the same window params, so invalidate
      // any matching status reads to pick up the freshly queued job state.
      void queryClient.invalidateQueries({ predicate: (q) => isTimelapseStatusKey(q.queryKey) });
    },
  });

  // `mutation` is a fresh object on every render, so depending on it
  // directly would re-create `requestVideo` each render and break any
  // upstream `useCallback` that depends on it. Pin `mutateAsync` in a
  // ref and expose a stable wrapper that always delegates to the latest
  // mutation instance.
  const mutateAsyncRef = useRef(mutation.mutateAsync);
  mutateAsyncRef.current = mutation.mutateAsync;
  const requestVideo = useCallback((body: RequestTimelapseVideoBody) => mutateAsyncRef.current(body), []);

  return {
    requestVideo,
    isPending: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
  };
}

function isTimelapseStatusKey(key: readonly unknown[]): boolean {
  const head = key[0];
  if (typeof head !== "object" || head === null) return false;
  const candidate = head as { resource?: unknown };
  return candidate.resource === "timelapse/video";
}
